import React from 'react';
import { Box } from '@mui/material';
import { PayslipDetailDto, PayslipTemplateDto, SalaryComponentType } from '../../../types/payrollTypes';

const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const defaultTemplate: PayslipTemplateDto = {
  id: 'runtime-default',
  name: 'Default Payslip',
  headerTemplate: '{{agencyName}}\nPayslip for {{month}} {{year}}\nEmployee: {{employeeName}} ({{employeeCode}})\nDepartment: {{department}}\nDesignation: {{designation}}',
  bodyTemplate: 'Earnings\n{{earningsTable}}\n\nDeductions\n{{deductionsTable}}\n\nGross Salary: {{grossSalary}}\nTotal Earnings: {{totalEarnings}}\nTotal Deductions: {{totalDeductions}}\nNet Salary: {{netSalary}}',
  footerTemplate: 'Generated by Evita HRMS. Authorized Signatory: {{authorizedSignatory}}',
  isDefault: true,
};

const currency = (value?: number | null) => `INR ${(value ?? 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const formatDate = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-IN');
};

const amountRowsToText = (rows: { componentName: string; amount: number }[]) =>
  rows.map((row) => `${row.componentName}: ${currency(row.amount)}`).join('\n');

const amountRowsToTable = (rows: { componentName: string; amount: number }[]) => (
  <table style={{ width: '100%', borderCollapse: 'collapse', margin: '6px 0 12px' }}>
    <tbody>
      {rows.map((row, index) => (
        // An adjustment row can carry the same label as a salary component, so the
        // name alone is not a unique key.
        <tr key={`${row.componentName}-${index}`}>
          <td style={{ border: '1px solid #d5dbe7', padding: '7px 9px' }}>{row.componentName}</td>
          <td style={{ border: '1px solid #d5dbe7', padding: '7px 9px', textAlign: 'right', fontWeight: 700 }}>{currency(row.amount)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const earningComponentTypes = [
  SalaryComponentType.Earning,
  SalaryComponentType.Allowance,
  SalaryComponentType.ReimbursableComponent,
  SalaryComponentType.BaseComponent,
  SalaryComponentType.VariablePay,
  SalaryComponentType.Overtime,
];

const deductionComponentTypes = [
  SalaryComponentType.Deduction,
  SalaryComponentType.TaxComponent,
  SalaryComponentType.LeaveComponent,
];

const collectDocumentStyles = (): string => {
  try {
    return Array.from(document.styleSheets)
      .map((sheet) => {
        try {
          return Array.from(sheet.cssRules).map((rule) => rule.cssText).join('\n');
        } catch {
          // Cross-origin stylesheets (fonts, CDNs) can't be read; skip them.
          return '';
        }
      })
      .join('\n');
  } catch {
    return '';
  }
};

export const buildPayslipPrintHtml = (content: string) => `
  <html>
    <head>
      <title>Payslip</title>
      <style>${collectDocumentStyles()}</style>
      <style>
        @page { size: A4; margin: 6mm; }
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
        body { font-family: Arial, sans-serif; margin: 0; padding: 0; color: #1f2937; font-size: 12px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        table { page-break-inside: avoid; margin-bottom: 8px !important; }
        table td, table th { padding: 4px 7px !important; }
        .payslip-template-renderer { page-break-inside: avoid; break-inside: avoid; }
        .payslip-header { padding: 8px 12px !important; }
        .payslip-body { padding: 8px 12px !important; }
        .payslip-header-template { margin-bottom: 8px !important; line-height: 1.4 !important; }
        .payslip-footer { padding-top: 6px !important; }
        .payslip-stamp-block { page-break-inside: avoid; break-inside: avoid; page-break-before: avoid; margin-top: 6px !important; }
        .payslip-stamp-image { height: 50px !important; max-width: 110px !important; }
        .payslip-stamp-caption { margin-top: 2px !important; font-size: 10px !important; }
      </style>
    </head>
    <body>${content}</body>
  </html>
`;

interface Props {
  detail: PayslipDetailDto;
  template?: PayslipTemplateDto | null;
}

const PayslipTemplateRenderer: React.FC<Props> = ({ detail, template }) => {
  const activeTemplate = template ?? detail.template ?? defaultTemplate;
  const earnings = detail.components.filter((component) => earningComponentTypes.includes(component.type));
  const deductions = detail.components.filter((component) => deductionComponentTypes.includes(component.type));
  const allComponents = [...earnings, ...deductions];

  const tokenValues: Record<string, string> = {
    agencyName: detail.companyName || 'Company',
    companyName: detail.companyName || 'Company',
    companyAddress: detail.companyAddress || '',
    companyCity: detail.companyCity || '',
    companyState: detail.companyState || '',
    companyCountry: detail.companyCountry || '',
    companyPostalCode: detail.companyPostalCode || '',
    month: monthNames[detail.month] || String(detail.month),
    year: String(detail.year),
    generatedDate: formatDate(detail.generatedAt),
    employeeName: detail.employeeName || '',
    employeeCode: detail.employeeCode || '',
    department: detail.department || '',
    designation: detail.designation || '',
    dateOfJoining: formatDate(detail.dateOfJoining),
    panNumber: detail.panNumber || '',
    officialEmail: detail.officialEmail || '',
    workLocation: detail.workLocation || '',
    bankAccountNumber: detail.bankAccountNumber || '',
    ifscCode: detail.ifscCode || '',
    payrollGroup: detail.payrollGroup || '',
    scheduledWorkingDays: String(detail.scheduledWorkingDays ?? detail.workingDays ?? ''),
    presentDays: String(detail.presentDays ?? ''),
    halfDayDays: String(detail.halfDayDays ?? 0),
    paidLeaveDays: String(detail.paidLeaveDays ?? detail.leaveDays ?? ''),
    unpaidLeaveDays: String(detail.unpaidLeaveDays ?? 0),
    absentDays: String(detail.absentDays ?? 0),
    lopDays: String(detail.lopDays ?? 0),
    payableDays: String(detail.payableDays ?? detail.workingDays ?? ''),
    workingDays: String(detail.workingDays ?? ''),
    leaveDays: String(detail.leaveDays ?? ''),
    overtimeHours: String(detail.overtimeHours ?? 0),
    overtimeAmount: currency(detail.overtimeAmount),
    lateMarkCount: String(detail.lateMarkCount ?? 0),
    lateMarkDeductionDays: String(detail.lateMarkDeductionDays ?? 0),
    lateMarkDeductionAmount: currency(detail.lateMarkDeductionAmount),
    grossSalary: currency(detail.grossSalary),
    totalEarnings: currency(detail.totalEarnings),
    totalDeductions: currency(detail.totalDeductions),
    netSalary: currency(detail.netSalary),
    netSalaryInWords: `${currency(detail.netSalary)} only`,
    authorizedSignatory: detail.authorizedSignatory || 'Payroll Manager',
    earningsTable: amountRowsToText(earnings),
    deductionsTable: amountRowsToText(deductions),
    componentsTable: amountRowsToText(allComponents),
  };

  const renderText = (value: string) => value.replace(/{{\s*([^}]+)\s*}}/g, (_, token: string) => {
    const key = token.trim();
    return tokenValues[key] ?? `{{${key}}}`;
  });

  return (
    <Box
      className="payslip-template-renderer"
      sx={{
        bgcolor: '#fff',
        color: '#1f2937',
        fontFamily: 'Arial, sans-serif',
        fontSize: 13,
        p: 3,
        '@media print': { p: 0 },
      }}
    >
      <Box sx={{ border: '1px solid #cbd5e1', borderRadius: 1, overflow: 'hidden' }}>
        <Box
          className="payslip-header"
          sx={{
            bgcolor: '#1f3a5f',
            color: '#fff',
            p: 2,
            display: 'flex',
            flexDirection: detail.logoPosition === 'Right' ? 'row-reverse' : 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {detail.companyLogoUrl && (
              <Box
                component="img"
                src={detail.companyLogoUrl}
                alt={detail.companyName || 'Company logo'}
                sx={{ height: 48, maxWidth: 140, objectFit: 'contain', bgcolor: '#fff', borderRadius: 0.5, p: 0.5 }}
              />
            )}
            <Box>
              <Box sx={{ fontSize: 20, fontWeight: 800 }}>{detail.companyName || 'Company'}</Box>
              {detail.companyAddress && <Box sx={{ fontSize: 12, opacity: 0.88, mt: 0.5 }}>{detail.companyAddress}</Box>}
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Box sx={{ fontSize: 12, opacity: 0.85 }}>Net Pay</Box>
            <Box sx={{ fontSize: 22, fontWeight: 900 }}>{currency(detail.netSalary)}</Box>
          </Box>
        </Box>

        <Box className="payslip-body" sx={{ p: 2 }}>
          <Box className="payslip-header-template" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6, fontWeight: 600, mb: 2 }}>
            {renderText(activeTemplate.headerTemplate)}
          </Box>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 14 }}>
            <tbody>
              {[
                ['Employee Code', detail.employeeCode, 'Department', detail.department],
                ['Employee Name', detail.employeeName, 'Designation', detail.designation],
                ['Date of Joining', formatDate(detail.dateOfJoining), 'PAN', detail.panNumber],
                ['Working Days', String(detail.workingDays ?? ''), 'Payable Days', String(detail.payableDays ?? detail.workingDays ?? '')],
              ].map((row) => (
                <tr key={row[0]}>
                  <td style={{ border: '1px solid #d5dbe7', padding: '7px 9px', background: '#f8fafc', fontWeight: 700 }}>{row[0]}</td>
                  <td style={{ border: '1px solid #d5dbe7', padding: '7px 9px' }}>{row[1] || '-'}</td>
                  <td style={{ border: '1px solid #d5dbe7', padding: '7px 9px', background: '#f8fafc', fontWeight: 700 }}>{row[2]}</td>
                  <td style={{ border: '1px solid #d5dbe7', padding: '7px 9px' }}>{row[3] || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '16px 0' }}>
            <tbody>
              <tr>
                <td style={{ width: '50%', verticalAlign: 'top', padding: 0 }}>
                  <div style={{ background: '#e8f5ec', border: '1px solid #b8dfc2', padding: 8, fontWeight: 800 }}>Earnings</div>
                  {amountRowsToTable(earnings)}
                </td>
                <td style={{ width: '50%', verticalAlign: 'top', padding: 0 }}>
                  <div style={{ background: '#fff1f0', border: '1px solid #f0b8b2', padding: 8, fontWeight: 800 }}>Deductions</div>
                  {amountRowsToTable(deductions)}
                </td>
              </tr>
            </tbody>
          </table>

          <table style={{ width: '100%', borderCollapse: 'collapse', margin: '4px 0 14px' }}>
            <tbody>
              <tr>
                <td style={{ border: '1px solid #d5dbe7', padding: '8px 10px', background: '#f8fafc', fontWeight: 800 }}>Gross Salary</td>
                <td style={{ border: '1px solid #d5dbe7', padding: '8px 10px', textAlign: 'right', fontWeight: 800 }}>{currency(detail.grossSalary)}</td>
                <td style={{ border: '1px solid #d5dbe7', padding: '8px 10px', background: '#f8fafc', fontWeight: 800 }}>Total Deductions</td>
                <td style={{ border: '1px solid #d5dbe7', padding: '8px 10px', textAlign: 'right', fontWeight: 800 }}>{currency(detail.totalDeductions)}</td>
              </tr>
              <tr>
                <td colSpan={3} style={{ border: '1px solid #b8dfc2', padding: '10px', background: '#eef7f1', fontWeight: 900 }}>Net Salary</td>
                <td style={{ border: '1px solid #b8dfc2', padding: '10px', background: '#eef7f1', textAlign: 'right', fontWeight: 900 }}>{currency(detail.netSalary)}</td>
              </tr>
            </tbody>
          </table>

          <Box className="payslip-footer" sx={{ borderTop: '1px solid #d5dbe7', pt: 1.5, whiteSpace: 'pre-line', color: '#64748b', fontSize: 12, textAlign: 'center' }}>
            {renderText(activeTemplate.footerTemplate)}
          </Box>

          {detail.companyStampUrl && (
            <Box
              className="payslip-stamp-block"
              sx={{
                display: 'flex',
                justifyContent: detail.stampPosition === 'Left' ? 'flex-start' : 'flex-end',
                mt: 2,
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  component="img"
                  className="payslip-stamp-image"
                  src={detail.companyStampUrl}
                  alt="Authorized stamp and signature"
                  sx={{ height: 80, maxWidth: 160, objectFit: 'contain' }}
                />
                <Box className="payslip-stamp-caption" sx={{ fontSize: 11, color: '#64748b', mt: 0.5 }}>Authorized Signatory</Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PayslipTemplateRenderer;
